import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CsvHeaderCell from './CsvHeaderCell';
import CsvBodyRow from './CsvBodyRow';
import {
  ROW_HEIGHT,
  DEFAULT_COL_WIDTH,
  MIN_COL_WIDTH,
  OVERSCAN,
  getScrollParent,
  buildRows,
  rebalanceWidths,
  scaleWidths,
} from './csvTableUtils';

export const EddCSVTable = memo(function EddCSVTable({ columns, errors }) {
  const columnTitles = useMemo(() => Object.keys(columns), [columns]);
  const [columnWidths, setColumnWidths] = useState({});
  const containerRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!columnTitles.length) {
      return;
    }

    const containerWidth = containerRef.current ? containerRef.current.clientWidth : 0;
    const perColumn =
      containerWidth > 0
        ? Math.max(MIN_COL_WIDTH, Math.floor(containerWidth / columnTitles.length))
        : DEFAULT_COL_WIDTH;

    setColumnWidths(Object.fromEntries(columnTitles.map(t => [t, perColumn])));
  }, [columnTitles]);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    let prevWidth = containerRef.current.clientWidth;
    const observer = new ResizeObserver(entries => {
      const newWidth = Math.round(entries[0]?.contentRect?.width ?? 0);

      if (!newWidth || newWidth === prevWidth) {
        return;
      }

      prevWidth = newWidth;
      setColumnWidths(prev => scaleWidths(prev, newWidth));
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [columnTitles]);

  const rows = useMemo(() => buildRows(columns, columnTitles), [columns, columnTitles]);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(globalThis.window.innerHeight);

  useEffect(() => {
    if (!bodyRef.current) {
      return;
    }

    const scrollEl = getScrollParent(bodyRef.current);
    const isWindow = scrollEl === globalThis.window;

    const handleScroll = () => {
      if (!bodyRef.current) {
        return;
      }

      setScrollTop(Math.max(0, -bodyRef.current.getBoundingClientRect().top));
    };

    const handleViewportResize = () => {
      setViewportHeight(isWindow ? window.innerHeight : scrollEl.clientHeight);
      handleScroll();
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleViewportResize);
    setViewportHeight(isWindow ? window.innerHeight : scrollEl.clientHeight);
    scrollEl.scrollTo?.(0, 0);
    handleScroll();

    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleViewportResize);
    };
  }, [columnTitles]);

  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    rows.length - 1,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN,
  );
  const handleResize = useCallback((title, newWidth) => {
    setColumnWidths(prev =>
      rebalanceWidths(
        prev,
        title,
        newWidth,
        containerRef.current ? containerRef.current.clientWidth : 0,
      ),
    );
  }, []);
  const virtualItems = [];

  for (let i = startIndex; i <= endIndex; i += 1) {
    virtualItems.push(i);
  }

  return (
    <div className="flex flex-col items-center">
      <div ref={containerRef} className="flex w-full min-w-0 justify-center items-center">
        <div className="w-full mt-8 shadow-2xl shadow-[#bebebe]">
          <div>
            <div className="flex sticky top-0 z-10">
              {columnTitles.map(title => (
                <CsvHeaderCell
                  key={title}
                  title={title}
                  width={columnWidths[title] || DEFAULT_COL_WIDTH}
                  onResize={handleResize}
                />
              ))}
            </div>
            <div ref={bodyRef} className="[overflow-anchor:none]">
              <div style={{ height: startIndex * ROW_HEIGHT }} />
              {virtualItems.map(index => {
                const row = rows[index];
                const error = errors[`${index + 2}`];

                return (
                  <div key={row[columnTitles[0]].id} className="flex h-10">
                    <CsvBodyRow
                      row={row}
                      columnTitles={columnTitles}
                      columnWidths={columnWidths}
                      error={error}
                    />
                  </div>
                );
              })}
              <div style={{ height: Math.max(0, (rows.length - endIndex - 1) * ROW_HEIGHT) }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

EddCSVTable.propTypes = {
  columns: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  errors: PropTypes.object.isRequired,
};
